/**
 * Tiny event-bus-driven state store. Server-authoritative; clock-reconciled.
 */

const state = {
  config: null,
  round: null,            // current round (server view)
  remainingMs: 0,
  bettingEndsAt: 0,       // absolute server deadline (ms epoch); 0 = none
  spinEndsAt: 0,          // absolute server deadline (ms epoch); 0 = none
  betsBySlot: {},         // aggregate from server (slotId -> amount)
  myBets: {},             // slotId -> amount (this round, optimistic)
  selectedAmount: 0,
  balance: null,          // diamonds (best-effort, refreshed from server-driven balance returned by /bet)
  status: 'idle',         // idle | betting | spinning | result
  winningSlotId: null,
  winners: [],
  lastFinishedRound: null,
  audioMuted: false,
  // Session-only running totals (since this game session opened)
  sessionWin: 0,           // total diamonds won by me since open ("win today")
  recent: [],              // recent winning slot emojis (newest first), max 8
  activity: [],            // 24h per-user activity totals from monkey_wheel_activity
  activityUpdatedAt: 0,
  // Server clock reconciliation: server-time = clientNow + clockOffset.
  // Updated on every payload that carries serverNow (round events,
  // /round/current, monkeywheel_time_sync heartbeat, monkeywheel_round_snapshot).
  // The clockOffset is a *fallback* only: visible countdown is driven by
  // serverRemainingMs minus a monotonic delta since lastSyncReceivedAt, which
  // is immune to clock-offset bugs. See applyServerNow() for details.
  clockOffset: 0,
  _clockOffsetSampled: false,
  _clockSampleCount: 0,
  _clockBestRtt: Infinity,
  _clockLastRttMs: null,
  // Server-authoritative countdown source. Updated on every payload that
  // carries remainingMs (round_started, time_sync, round_snapshot, etc.).
  // The HUD reads `serverRemainingMs - (Date.now() - lastSyncReceivedAt)`.
  serverRemainingMs: 0,
  lastSyncReceivedAt: 0,
  lastSyncSource: null, // 'http' | 'ws_time_sync' | 'ws_round_event' | 'ws_snapshot'
};

const listeners = new Set();

export function getState() { return state; }

export function setState(patch) {
  Object.assign(state, patch);
  listeners.forEach((fn) => fn(state));
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function clearMyBets() { state.myBets = {}; }

/**
 * Reconcile the client clock with the server. Called whenever a payload
 * carries `serverNow`. `rttMs` is the measured round-trip when known
 * (HTTP fetches, WS pongs), else null. EMA-blended, weighted by RTT.
 */
export function applyServerNow(serverNow, rttMs) {
  if (!serverNow || typeof serverNow !== 'number') return;
  const clientRecvAt = Date.now();
  const halfRtt = typeof rttMs === 'number' && isFinite(rttMs) && rttMs >= 0 ? rttMs / 2 : 0;
  const sample = (serverNow + halfRtt) - clientRecvAt;
  state._clockSampleCount = (state._clockSampleCount || 0) + 1;
  if (typeof rttMs === 'number' && isFinite(rttMs)) {
    state._clockLastRttMs = rttMs;
    if (rttMs < state._clockBestRtt) state._clockBestRtt = rttMs;
  }

  if (!state._clockOffsetSampled) {
    state.clockOffset = sample;
    state._clockOffsetSampled = true;
  } else {
    const rttForWeight = typeof rttMs === 'number' && isFinite(rttMs) ? rttMs : 350;
    const weight = Math.max(0.1, Math.min(0.6, 1 - rttForWeight / 500));
    state.clockOffset = state.clockOffset * (1 - weight) + sample * weight;
  }
}

/**
 * Apply a server payload that carries authoritative `remainingMs`. The HUD
 * countdown reads this minus the monotonic delta since `lastSyncReceivedAt`,
 * making it immune to clock-offset drift.
 */
export function applyServerRemaining(remainingMs, source) {
  if (typeof remainingMs !== 'number' || !isFinite(remainingMs)) return;
  state.serverRemainingMs = Math.max(0, remainingMs);
  state.lastSyncReceivedAt = Date.now();
  state.lastSyncSource = source || 'unknown';
}

/**
 * Authoritative remaining ms in the current phase, derived from the most
 * recent server payload minus monotonic time elapsed since it arrived.
 */
export function authoritativeRemainingMs() {
  if (!state.lastSyncReceivedAt) return 0;
  const elapsed = Date.now() - state.lastSyncReceivedAt;
  return Math.max(0, state.serverRemainingMs - elapsed);
}

/** Server-aligned current time in ms epoch. */
export function serverNow() {
  return Date.now() + state.clockOffset;
}
